import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '../common/Breadcrumb';
import { usePayments } from '../../context/PaymentContext';
import { usePaymentMethods } from '../../context/PaymentMethodContext';
import { useInvoices } from '../../context/InvoiceContext';
import { useLoading } from '../../context/LoadingContext';
import { useRoutePath } from '../../utils/getRoutePath';
import { formatCurrency } from '../../utils/format';

const schema = (totalDue = 0) => yup.object().shape({
  payment_method_id: yup.string().required('Payment method is required'),
  memo: yup.string().nullable(),
  invoice_id: yup.string().required('Invoice is required'),
  amount: yup
      .number()
      .typeError('Amount must be a number')
      .required('Amount is required')
      .min(0, 'Amount cannot be negative')
      .max(totalDue, `Amount cannot exceed total due (${totalDue})`),
  
});

const PaymentForm = () => {
  const getRoutePath = useRoutePath();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { addPayment, updatePayment, getPaymentById } = usePayments();
  const { getPaymentMethods } = usePaymentMethods();
  const { getInvoices,getInvoiceById } = useInvoices();
  const { showLoading, hideLoading } = useLoading();

  const [isLoading, setIsLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const hasLoaded = useRef(false);
  const [hasInvoiceParam, setHasInvoiceParam] = useState(false);
  const [editData, setEditData] = useState(null);
  const invRef = useRef(false);
  const payRef = useRef(false);
  const [amountDue,setAmountDue] = useState(0);
  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      opacity: state.data.value === '' ? 0.5 : 1
    }),
    singleValue: (provided, state) => ({
      ...provided,
      opacity: state.data.value === '' ? 0.5 : 1
    })
  };

  const getDefaultValues = useCallback((existingData = null) => {
    const data = existingData || {};
    
    return {
      payment_method_id: String(data?.payment_method_id || ''),
      memo: data?.memo || '',
      invoice_id: String(data?.invoice_id || ''),
      amount: Number(data?.amount || 0),
      amount_due: Number(data?.amount_due || 0),
      total: Number(data?.total || 0),
     
    };
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    watch,
    reset,
    trigger
  } = useForm({
    resolver: yupResolver(schema(amountDue)),
    defaultValues: getDefaultValues(editData),
    mode: 'onChange'
  });

  let selectedInvoiceId = watch('invoice_id');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const enteredAmount = watch('amount');
   
  useEffect(() => {
    if(!id){
      reset(getDefaultValues());
      setValue('invoice_id', '');
      setSelectedInvoice(null);
    }
  }, [id,reset,getDefaultValues,setValue]);

  useEffect(() => {
    const fetchInvoice = async () => {
      
      if (!selectedInvoiceId) return;
  
      let inv = invoices?.find(x => Number(x.id) === Number(selectedInvoiceId));
      if (!inv) {
        const response = await getInvoiceById(selectedInvoiceId);
        const data = await Promise.resolve(response?.data);
        if (response?.status === 200 && data?.id) {
          inv = data;
        }
      }
  
      if (inv) {
        setAmountDue(Number(inv?.job?.total_due || 0));
        setSelectedInvoice(inv);
      }
    };
  
    fetchInvoice();
  }, [selectedInvoiceId, invoices, getInvoiceById]);

  const totals = useMemo(() => {
  
    if (!selectedInvoice) {
      return { totalApply: 0, totalInvoiceBalance: 0 };
    }
    const apply = Number(enteredAmount || 0);
    const due = Number(selectedInvoice?.job?.total_due || 0);
    let totalInvoiceBalance = Number(Math.max(due - apply, 0).toFixed(2));  
     
    return {
      totalApply: Number(apply.toFixed(2)),
      totalInvoiceBalance: totalInvoiceBalance
    };
   
  }, [selectedInvoice, enteredAmount]);

  const handlePaymentMethodChange = (selectedOption) => {
    setValue('payment_method_id', selectedOption?.value || '');
    trigger('payment_method_id');
  };

  const addOrReplaceInvoiceRow = useCallback((invoice) => {
    // Only one invoice row at a time
    setValue('invoice_id', invoice.id);
    setValue('amount', invoice?.job?.total_due);
  }, [setValue]);
 

  const handleInvoiceSelect = useCallback(async (selected) => {
    
    const invoiceId = typeof selected === 'object' ? selected.value : selected;
    setValue('invoice_id', invoiceId);
    if (invoiceId) {
      const list = await getInvoiceById(invoiceId);
      
      if (list?.status === 200 && list?.data) {
        addOrReplaceInvoiceRow(list.data);
      }
     
      trigger('invoice_id');
    } else {
      setValue('invoice_id', '');
      trigger('invoice_id');
    }
  }, [getInvoiceById, addOrReplaceInvoiceRow, setValue, trigger]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const pm = await getPaymentMethods(0, { all: true, active: true });
        setPaymentMethods(pm?.data || []);

        const inv = await getInvoices(0, { all: true, active: 1,is_full_paid:'false' });
        setInvoices(inv?.data || []);
      } catch (e) {
        toast.error('Failed to load form data');
      }
    };
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      loadData();
    }
  }, [getPaymentMethods, getInvoices]);

  useEffect(() => {
    const initFromQuery = async () => {
      const params = new URLSearchParams(location.search);
      const invoiceIdParam = params.get('invoiceId');
      const hasParam = Boolean(invoiceIdParam);
      setHasInvoiceParam(hasParam);
      if (invoiceIdParam) {
        const invList = await getInvoiceById(invoiceIdParam);
        if (invList?.status === 200 && invList?.data) {
          addOrReplaceInvoiceRow(invList.data);
          setValue('invoice_id', invList.data.id);
          trigger('invoice_id');
        }
      }
    };
    if(invRef.current === false){
      invRef.current = true;
      initFromQuery();
    }
  }, [location.search, getInvoiceById, addOrReplaceInvoiceRow, setValue, trigger,invRef]);

  useEffect(() => {
    const fetchEdit = async () => {
      if (!id) return;
      showLoading();
      try {
        const response = await getPaymentById(id);
        
        if (response?.status === 200 && response?.data) {
          const p = response.data;
           
          setEditData(p);
          reset(getDefaultValues(p))
           
        }
      } catch (e) {
        //toast.error('Error fetching payment data');
      } finally {
        hideLoading();
      }
    };
    if(payRef.current === false){
      fetchEdit();
      payRef.current = true
    }
    
  }, [id, getPaymentById, reset, showLoading, hideLoading,setEditData,getDefaultValues]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    showLoading();
    try {
      const payload = {
        payment_method_id: data.payment_method_id,
        memo: data.memo || '',
        invoice_id: data.invoice_id,
        amount: data.amount,
        total_balance: totals.totalInvoiceBalance
      };
      if (id) {
        const response = await updatePayment(id, payload);
        if (response.status === 200) {
          toast.success('Payment updated successfully');
          reset();
          navigate(getRoutePath('/payments'));
        } else if (response.errors) {
          Object.entries(response.errors).forEach(([field, message]) => {
            setError(field, { type: 'manual', message });
          });
        } else if (response.error?.message) {
          setError('general', { type: 'manual', message: response.error.message });
        }
      } else {
        const response = await addPayment(payload);
        if (response.status === 200) {
          toast.success('Payment created successfully');
          reset();
          navigate(getRoutePath('/payments'));
        } else if (response.errors) {
          Object.entries(response.errors).forEach(([field, message]) => {
            setError(field, { type: 'manual', message });
          });
        } else if (response.error?.message) {
          setError('general', { type: 'manual', message: response.error.message });
        }
      }
    } catch (error) {
      toast.error('Failed to save payment. Please try again.');
    } finally {
      setIsLoading(false);
      hideLoading();
    }
  };

  const invoiceOptions = useMemo(() => {
    return [
      { value: '', label: 'Choose Invoice' },
      ...invoices.map(inv => ({ value: inv.id, label: `${inv.invoice_number}` }))
    ];
  }, [invoices]);

  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumb />
     
      <div className="mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {id ? 'Edit Payment' : 'Create New Payment'}
        </h1>
      </div>
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Payment Information</h2>
         
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" autoComplete="off">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Payment Method <span className="text-red-500">*</span></label>
                <Select
                  name="payment_method_id"
                  isSearchable={true}
                  value={paymentMethods
                    ?.map(pm => ({ value: String(pm.id), label: pm.name }))
                    .find(option => option.value === String(watch('payment_method_id')))}
                  onChange={handlePaymentMethodChange}
                  options={[
                    { value: '', label: 'Choose Payment Method' },
                    ...paymentMethods.map(pm => ({ value: String(pm.id), label: pm.name }))
                  ]}
                  styles={{
                    ...customStyles,
                    control: (base) => ({
                      ...base,
                      minHeight: '42px',
                      borderColor: errors.payment_method_id ? '#ef4444' : '#d1d5db',
                      '&:hover': { borderColor: errors.payment_method_id ? '#ef4444' : '#9ca3af' }
                    })
                  }}
                  placeholder="Select Payment Method"
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
                {errors.payment_method_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.payment_method_id.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Payment Memo</label>
                <input
                  type="text"
                  {...register('memo')}
                  className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                  placeholder="Enter memo (optional)"
                />
                {errors.memo && (
                  <p className="mt-1 text-sm text-red-600">{errors.memo.message}</p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Invoice Information</h3>

            {!id && !hasInvoiceParam && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700">Select Invoice <span className="text-red-500">*</span></label>
                <Select
                  isSearchable={true}
                  value={invoiceOptions.find(option => option.value === watch('invoice_id'))}
                  onChange={handleInvoiceSelect}
                  options={invoiceOptions}
                  styles={{
                    ...customStyles,
                    control: (base) => ({
                      ...base,
                      minHeight: '42px',
                      borderColor: errors.invoice_id ? '#ef4444' : '#d1d5db',
                      '&:hover': { borderColor: errors.invoice_id ? '#ef4444' : '#9ca3af' }
                    })
                  }}
                  placeholder="Select Invoice"
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
                {errors.invoice_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.invoice_id.message}</p>
                )}
              </div>
            )}

            {selectedInvoice && watch('invoice_id') && (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                  <h4 className="text-lg font-medium text-gray-900">Invoice Details</h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice#</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice Total</th>
                        {id &&<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Amount</th>}
                        {id && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount Paid</th>}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount Due</th>
                        {!id && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount To Apply</th>}
                        {!id && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                        }
                        <th className="px-6 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr> 
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{selectedInvoice?.formated_created_at || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <Link to={getRoutePath(`/invoices/view/${selectedInvoice?.id}`)} className="text-[#009efb] hover:text-[#0089db]" target="_blank">{selectedInvoice?.invoice_number || '-'}</Link>
                        </td> 
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{selectedInvoice?.job?.description || selectedInvoice?.description || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${selectedInvoice?.job?.job_total ? Number(selectedInvoice?.job?.job_total).toFixed(2) : '0.00'}</td>
                        {id && <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(Number(editData?.total).toFixed(2) || '0.00')}</td>}
                        {id && <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(Number(editData?.amount).toFixed(2) || '0.00')}</td>}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${selectedInvoice?.job?.total_due ? Number(selectedInvoice?.job?.total_due).toFixed(2) : '0.00'}</td>
                      
                        {!id && <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <input
                            type="number"
                            className={`${errors?.amount ? 'border-red-500' : 'border-gray-300'} w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            value={enteredAmount ?? ''}
                            min="0"
                            step="0.01"
                            onChange={(e) => {
                              setValue('amount', e.target.value);
                              trigger('amount');
   
                            }}
                            disabled={id ? true : false}
                            readOnly={!!id}
                            placeholder='Enter Amount Here'
                          />
                          {errors?.amount && (
                            <p className="mt-1 text-xs text-red-600">{errors?.amount?.message}</p>
                          )}
                        </td> }
                        {!id && <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 bg-blue-50">${totals.totalInvoiceBalance.toFixed(2)}</td>}
                      </tr>
                    </tbody>
                  </table>
                </div>
                 
                {!id &&
                <div className="px-4 py-5 sm:px-6 border-t border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-sm text-gray-700">
                      <div className="font-medium">Total:</div>
                      <div className="mt-1">${selectedInvoice?.job?.total_due ? Number(selectedInvoice?.job?.total_due).toFixed(2) : '0.00'}</div>
                    </div>
                    <div className="text-sm text-gray-700">
                      <div className="font-medium">Amount of payment</div>
                      <div className="mt-1">${totals.totalApply.toFixed(2)}</div>
                    </div>
                      <div className="text-sm text-gray-700">
                      <div className="font-medium">Total Balance</div>
                      <div className="mt-1">${totals.totalInvoiceBalance.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
                 }
              </div>
             
            )}
 
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200"
              disabled={isLoading}
            >
              {isLoading ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4 mr-2" />
                  {id ? 'Update' : 'Save'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentForm;


